import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../utils/supabaseClient";
import { PRODUCT_CERTS as PRODUCT_IMPACTS, NO_FILE_CERTS, productCertLabel } from "../constants/productCerts";
import { useConfirm } from "../components/ConfirmDialog";
import "./AssessmentSummary.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

export default function AssessmentSummary() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { supplierInfo } = useAuth();
  const { alert } = useConfirm();
  const supplierId = supplierInfo?.supplier_id;

  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState(null);
  const [product, setProduct] = useState(null);
  const [formData, setFormData] = useState(null);

  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({});

  // Product-level social impact state
  const [selectedProduct, setSelectedProduct] = useState([]);
  const [productOther, setProductOther] = useState("");

  // Per-certification file uploads: { certId: { name, size, url, path } }
  const [certFiles, setCertFiles] = useState({});
  const [uploadingCert, setUploadingCert] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!supplierId || !productId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: assessmentResponse } = await axios.get(
          `${API_BASE_URL}/lca/assessments`,
          { params: { supplier_id: supplierId, product_id: productId } }
        );

        const asmnt = assessmentResponse?.data;
        if (!asmnt) {
          setLoading(false);
          return;
        }
        setAssessment(asmnt);

        // Fetch product details
        try {
          const { data: productResponse } = await axios.get(
            `${API_BASE_URL}/products/${productId}`,
            { params: { supplier_id: supplierId } }
          );
          setProduct(productResponse?.data || null);
        } catch (e) {
          console.warn("Could not load product:", e);
        }

        // Transform assessment to readable formData
        let parts = asmnt.parts;
        if (typeof parts === "string") {
          try { parts = JSON.parse(parts); } catch { parts = []; }
        }
        if (!Array.isArray(parts)) parts = [];

        const transportLegs = Array.isArray(asmnt.transport_legs) && asmnt.transport_legs.length > 0
          ? asmnt.transport_legs
          : [{ mode: asmnt.transportation_mode, distance_km: asmnt.transportation_distance_km }];

        setFormData({
          productInfo: {
            name: asmnt.product_name || "",
            weight: asmnt.weight_grams || 0,
            category: asmnt.parent_category_slug || "",
            sku: asmnt.sku || "",
            productType: asmnt.product_type || "manufactured",
            supplierDataAvailable: !!asmnt.supplier_data_available,
          },
          materialAcquisition: {
            assessmentType: asmnt.material_assessment_type || "finished",
            parts,
            finishedProduct: {
              subcategorySlug: asmnt.finished_subcategory_slug || "",
              productSlug: asmnt.finished_product_slug || "",
              quality: asmnt.finished_quality || "",
              composition: asmnt.finished_composition || [],
            },
          },
          packaging: {
            materials: Array.isArray(asmnt.packaging_materials) ? asmnt.packaging_materials : [],
          },
          assemblyDistribution: {
            manufacturing: {
              energyConsumption: asmnt.manufacturing_energy_consumption_kwh || "",
              assemblyLocation: typeof asmnt.assembly_location === "object"
                ? asmnt.assembly_location?.code || ""
                : asmnt.assembly_location || "",
              energySource: asmnt.energy_source || "grid",
            },
            transportation: transportLegs,
          },
          decorationProcesses: Array.isArray(asmnt.decoration_processes) ? asmnt.decoration_processes : [],
          emissions: {
            materials: Number(asmnt.material_emissions_kg || 0),
            packaging: Number(asmnt.packaging_emissions_kg || 0),
            decoration: Number(asmnt.decoration_emissions_kg || 0),
            transport: Number(asmnt.transport_emissions_kg || 0),
            manufacturing: Number(asmnt.manufacturing_emissions_kg || 0),
            total: Number(asmnt.total_emissions_kg || 0),
          },
        });

        // Load any existing social impact data from assessment
        if (asmnt.social_impact) {
          const si = typeof asmnt.social_impact === "string" ? JSON.parse(asmnt.social_impact) : asmnt.social_impact;
          if (si.product) setSelectedProduct(si.product);
          if (si.productOther) setProductOther(si.productOther);
          if (si.certFiles) setCertFiles(si.certFiles);
        }
      } catch (err) {
        console.error("Error fetching assessment:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [supplierId, productId]);

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleTag = (list, setList, id) => {
    setList((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  // Every selected cert is saved and shows on the results page, with or
  // without its proof file — a file only makes the badge link to it.
  const certPayload = (selected, files) => {
    const product = selected.filter((id) => id !== "other" || productOther.trim() !== "");
    return { product, productOther: product.includes("other") ? productOther : "", certFiles: files };
  };

  // Upload file for a specific certification
  const handleCertFileUpload = async (certId, e) => {
    const file = e.target.files?.[0];
    if (!file || !supplierId) return;
    if (file.size > 10 * 1024 * 1024) {
      alert({ title: 'File too large', message: 'Please upload a file under 10MB.' });
      e.target.value = '';
      return;
    }

    setUploadingCert(certId);
    try {
      const timestamp = Date.now();
      const ext = file.name.split(".").pop();
      const filePath = `${supplierId}/social-impact-${certId}-${timestamp}.${ext}`;

      const { error } = await supabase.storage
        .from("lca-documents")
        .upload(filePath, file, { cacheControl: "3600", upsert: false });

      if (error) throw error;

      const nextCertFiles = {
        ...certFiles,
        [certId]: { name: file.name, size: file.size, path: filePath },
      };
      const nextSelected = selectedProduct.includes(certId)
        ? selectedProduct
        : [...selectedProduct, certId];
      setCertFiles(nextCertFiles);
      setSelectedProduct(nextSelected);

      // Persist immediately so DB is always in sync with storage.
      if (assessment?.id && supplierId) {
        await axios.post(`${API_BASE_URL}/lca/assessments/${assessment.id}/social-impact`, {
          supplier_id: supplierId,
          social_impact: certPayload(nextSelected, nextCertFiles),
        });
      }
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploadingCert(null);
    }
  };

  const removeCertFile = async (certId) => {
    const file = certFiles[certId];
    if (file?.path) {
      try {
        await supabase.storage.from("lca-documents").remove([file.path]);
      } catch (err) {
        console.warn("Failed to remove file:", err);
      }
    }
    const nextCertFiles = { ...certFiles };
    delete nextCertFiles[certId];
    const nextSelected = selectedProduct.filter((x) => x !== certId);
    setCertFiles(nextCertFiles);
    setSelectedProduct(nextSelected);

    // Persist immediately so DB and storage can't drift if user navigates away.
    if (assessment?.id && supplierId) {
      try {
        await axios.post(`${API_BASE_URL}/lca/assessments/${assessment.id}/social-impact`, {
          supplier_id: supplierId,
          social_impact: certPayload(nextSelected, nextCertFiles),
        });
      } catch (err) {
        console.error("Failed to persist cert removal:", err);
      }
    }
  };

  // Save social impact data and navigate
  const saveSocialImpact = async () => {
    if (!assessment?.id || !supplierId) return;

    const socialData = certPayload(selectedProduct, certFiles);

    try {
      await axios.post(`${API_BASE_URL}/lca/assessments/${assessment.id}/social-impact`, {
        supplier_id: supplierId,
        social_impact: socialData,
      });
    } catch (err) {
      // Non-blocking — social impact save is optional
      console.warn("Could not save social impact:", err);
    }
  };

  const handleViewResults = async () => {
    await saveSocialImpact();
    navigate(`/product-results/${productId}`);
  };

  const handleDoAnother = async () => {
    await saveSocialImpact();
    navigate("/products");
  };

  // Helpers
  const formatWeight = (grams) => {
    const g = Number(grams);
    if (!g) return "N/A";
    return g >= 1000 ? `${(g / 1000).toFixed(2)} kg` : `${g} g`;
  };

  const formatEmissions = (valueKg) => {
    const num = Number(valueKg);
    if (Number.isNaN(num)) return "-";
    return `${num.toFixed(num > 0 && num < 1 ? 3 : 2)} kg CO₂e`;
  };

  const formatEmissionsDetail = (valueKg) => {
    const num = Number(valueKg);
    if (Number.isNaN(num)) return "-";
    return `${num.toFixed(num > 0 && num < 1 ? 5 : 4)} kg CO₂e`;
  };

  const formatMode = (mode) => {
    const map = {
      air_international: "International Air",
      air_domestic: "Domestic Air",
      sea_container: "Sea Container",
      sea_bulk: "Sea Bulk",
      road_hgv_diesel: "Road (HGV Diesel)",
      rail_freight: "Rail Freight",
    };
    return mode ? (map[mode] || mode.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())) : "N/A";
  };

  const formatSlug = (slug) => {
    if (!slug) return "N/A";
    return slug.replace(/[-_]/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  // Get material preview text
  const getMaterialPreview = () => {
    if (!formData) return "";
    const type = formData.materialAcquisition.assessmentType;
    if (type === "finished") {
      const comp = formData.materialAcquisition.finishedProduct.composition;
      if (Array.isArray(comp) && comp.length > 0) {
        return comp.map((c) => (typeof c === "object" ? c.material : c)).filter(Boolean).join(", ");
      }
      return formatSlug(formData.materialAcquisition.finishedProduct.subcategorySlug);
    }
    const parts = formData.materialAcquisition.parts;
    if (parts.length === 0) return "No parts";
    return `${parts.length} part${parts.length > 1 ? "s" : ""} — ${formatWeight(formData.productInfo.weight)} total`;
  };

  const getTransportPreview = () => {
    if (!formData) return "";
    const legs = formData.assemblyDistribution.transportation;
    if (!legs.length) return "Not specified";
    if (legs.length === 1) return formatMode(legs[0]?.mode);
    return `${legs.length} legs`;
  };

  const getManufacturingPreview = () => {
    if (!formData) return "";
    const mfg = formData.assemblyDistribution.manufacturing;
    return mfg.assemblyLocation ? formatSlug(mfg.assemblyLocation) : "Not specified";
  };

  const getDecorationPreview = () => {
    if (!formData) return "";
    const decs = formData.decorationProcesses;
    if (!decs.length) return "None";
    return decs.map((d) => formatSlug(d.value)).join(", ");
  };

  const getPackagingPreview = () => {
    if (!formData) return "";
    const mats = formData.packaging.materials;
    if (!mats.length) return "None";
    return mats.map((pkg) => pkg.type || pkg.material || "Package").join(", ");
  };

  // Chevron SVG
  const ChevronDown = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // Check SVG for tags
  const CheckIcon = () => (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
      <path d="M2 5L4 7L8 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  if (loading) {
    return (
      <div className="summary-page">
        <div className="summary-loading">
          <div className="summary-spinner"></div>
          <p>Loading assessment summary...</p>
        </div>
      </div>
    );
  }

  if (!formData || !assessment) {
    return (
      <div className="summary-page">
        <div className="summary-loading">
          <p>Assessment not found.</p>
          <button className="summary-btn summary-btn-primary" onClick={() => navigate("/products")}>
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  const productName = product?.name || formData.productInfo.name || "Product";

  return (
    <div className="summary-page">
      <div className="summary-container">
        {/* Header */}
        <div className="summary-header">
          <div className="summary-header-top">
            <div>
              <h1 className="summary-product-name">Assessment Summary</h1>
              <p className="summary-subtitle">Review your assessment details before viewing results</p>
            </div>
            <div className="summary-badge">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 7L6.5 8.5L9 5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
              </svg>
              Assessment Complete
            </div>
          </div>
        </div>

        {/* Two columns */}
        <div className="summary-columns">
          {/* Left — Assessment Summary */}
          <div className="summary-card">
            <div className="summary-card-header">
              <h2 className="summary-card-title">{productName}</h2>
              <p className="summary-card-subtitle">
                {formatEmissions(formData.emissions.total)} total
              </p>
            </div>

            {/* Materials */}
            <div className="summary-section">
              <div className="summary-section-header" onClick={() => toggleSection("materials")}>
                <div className="summary-section-left">
                  <div className="summary-section-icon materials">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M9 2L2 6V12L9 16L16 12V6L9 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M9 8V16M9 8L2 4.5M9 8L16 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="summary-section-info">
                    <p className="summary-section-name">Materials</p>
                    <p className="summary-section-preview">{getMaterialPreview()}</p>
                  </div>
                </div>
                <div className={`summary-section-chevron ${expandedSections.materials ? "open" : ""}`}>
                  <ChevronDown />
                </div>
              </div>
              {expandedSections.materials && (
                <div className="summary-section-details">
                  <div className="summary-detail-row">
                    <span className="summary-detail-label">Assessment type</span>
                    <span className="summary-detail-value">
                      {formData.materialAcquisition.assessmentType === "finished" ? "Finished product" : "Parts-based"}
                    </span>
                  </div>
                  <div className="summary-detail-row">
                    <span className="summary-detail-label">Product weight</span>
                    <span className="summary-detail-value">{formatWeight(formData.productInfo.weight)}</span>
                  </div>
                  {formData.materialAcquisition.assessmentType === "finished" ? (
                    <>
                      {formData.materialAcquisition.finishedProduct.subcategorySlug && (
                        <div className="summary-detail-row">
                          <span className="summary-detail-label">Product type</span>
                          <span className="summary-detail-value">
                            {formatSlug(formData.materialAcquisition.finishedProduct.subcategorySlug)}
                          </span>
                        </div>
                      )}
                      {formData.materialAcquisition.finishedProduct.quality && (
                        <div className="summary-detail-row">
                          <span className="summary-detail-label">Quality tier</span>
                          <span className="summary-detail-value">
                            {formatSlug(formData.materialAcquisition.finishedProduct.quality)}
                          </span>
                        </div>
                      )}
                      {Array.isArray(formData.materialAcquisition.finishedProduct.composition) &&
                        formData.materialAcquisition.finishedProduct.composition.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <span className="summary-detail-label">Composition</span>
                            <div className="summary-parts-list">
                              {formData.materialAcquisition.finishedProduct.composition.map((comp, i) => (
                                <div key={i} className="summary-part-item">
                                  <span className="summary-part-name">
                                    {typeof comp === "object" ? comp.material : comp}
                                  </span>
                                  
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                    </>
                  ) : (
                    <>
                      {formData.materialAcquisition.parts.length > 0 && (
                        <div style={{ marginTop: 8 }}>
                          <span className="summary-detail-label">Parts</span>
                          <div className="summary-parts-list">
                            {formData.materialAcquisition.parts.map((part, i) => (
                              <div key={i} className="summary-part-item">
                                <span className="summary-part-name">
                                  {formatSlug(part.material_subcategory || part.material_category)}
                                </span>
                                <span className="summary-part-weight">
                                  {part.material_unit === 'per_item'
                                    ? `× ${part.quantity || 0}`
                                    : formatWeight(part.weight_grams)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  <div className="summary-detail-row" style={{ marginTop: 8 }}>
                    <span className="summary-detail-label">Material emissions</span>
                    <span className="summary-detail-value" style={{ color: "#16a34a", fontWeight: 600 }}>
                      {formatEmissionsDetail(formData.emissions.materials)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Manufacturing */}
            <div className="summary-section">
              <div className="summary-section-header" onClick={() => toggleSection("manufacturing")}>
                <div className="summary-section-left">
                  <div className="summary-section-icon manufacturing">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="2" y="8" width="4" height="8" rx="1" stroke="currentColor" strokeWidth="1.5" />
                      <rect x="7" y="4" width="4" height="12" rx="1" stroke="currentColor" strokeWidth="1.5" />
                      <rect x="12" y="2" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div className="summary-section-info">
                    <p className="summary-section-name">Manufacturing</p>
                    <p className="summary-section-preview">{getManufacturingPreview()}</p>
                  </div>
                </div>
                <div className={`summary-section-chevron ${expandedSections.manufacturing ? "open" : ""}`}>
                  <ChevronDown />
                </div>
              </div>
              {expandedSections.manufacturing && (
                <div className="summary-section-details">
                  <div className="summary-detail-row">
                    <span className="summary-detail-label">Manufacturing location</span>
                    <span className="summary-detail-value">
                      {formData.assemblyDistribution.manufacturing.assemblyLocation
                        ? formatSlug(formData.assemblyDistribution.manufacturing.assemblyLocation)
                        : "Not specified"}
                    </span>
                  </div>
                  <div className="summary-detail-row">
                    <span className="summary-detail-label">Energy source</span>
                    <span className="summary-detail-value">
                      {formatSlug(formData.assemblyDistribution.manufacturing.energySource)}
                    </span>
                  </div>
                  {formData.assemblyDistribution.manufacturing.energyConsumption && (
                    <div className="summary-detail-row">
                      <span className="summary-detail-label">Energy consumption</span>
                      <span className="summary-detail-value">
                        {formData.assemblyDistribution.manufacturing.energyConsumption} kWh
                      </span>
                    </div>
                  )}
                  <div className="summary-detail-row">
                    <span className="summary-detail-label">Manufacturing emissions</span>
                    <span className="summary-detail-value" style={{ color: "#7c3aed", fontWeight: 600 }}>
                      {formatEmissionsDetail(formData.emissions.manufacturing)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Transport */}
            <div className="summary-section">
              <div className="summary-section-header" onClick={() => toggleSection("transport")}>
                <div className="summary-section-left">
                  <div className="summary-section-icon transport">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M1 12H17M3 12V6C3 5.448 3.448 5 4 5H11L14 8V12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="6" cy="14" r="2" stroke="currentColor" strokeWidth="1.5" />
                      <circle cx="13" cy="14" r="2" stroke="currentColor" strokeWidth="1.5" />
                    </svg>
                  </div>
                  <div className="summary-section-info">
                    <p className="summary-section-name">Transport</p>
                    <p className="summary-section-preview">{getTransportPreview()}</p>
                  </div>
                </div>
                <div className={`summary-section-chevron ${expandedSections.transport ? "open" : ""}`}>
                  <ChevronDown />
                </div>
              </div>
              {expandedSections.transport && (
                <div className="summary-section-details">
                  {formData.assemblyDistribution.transportation.map((leg, i) => (
                    <React.Fragment key={i}>
                      {formData.assemblyDistribution.transportation.length > 1 && (
                        <div className="summary-detail-row">
                          <span className="summary-detail-label" style={{ fontWeight: 600, color: "#0a0e27" }}>
                            Leg {i + 1}
                          </span>
                          <span></span>
                        </div>
                      )}
                      <div className="summary-detail-row">
                        <span className="summary-detail-label">Mode</span>
                        <span className="summary-detail-value">{formatMode(leg.mode)}</span>
                      </div>
                      {(leg.distance_km || leg.distance) && (
                        <div className="summary-detail-row">
                          <span className="summary-detail-label">Distance</span>
                          <span className="summary-detail-value">
                            {Number(leg.distance_km || leg.distance).toLocaleString()} km
                          </span>
                        </div>
                      )}
                    </React.Fragment>
                  ))}
                  <div className="summary-detail-row" style={{ marginTop: 4 }}>
                    <span className="summary-detail-label">Transport emissions</span>
                    <span className="summary-detail-value" style={{ color: "#d97706", fontWeight: 600 }}>
                      {formatEmissionsDetail(formData.emissions.transport)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Decoration */}
            <div className="summary-section">
              <div className="summary-section-header" onClick={() => toggleSection("decoration")}>
                <div className="summary-section-left">
                  <div className="summary-section-icon decoration">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M14 2L16 4L6 14L2 16L4 12L14 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M11 5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div className="summary-section-info">
                    <p className="summary-section-name">Decoration</p>
                    <p className="summary-section-preview">{getDecorationPreview()}</p>
                  </div>
                </div>
                <div className={`summary-section-chevron ${expandedSections.decoration ? "open" : ""}`}>
                  <ChevronDown />
                </div>
              </div>
              {expandedSections.decoration && (
                <div className="summary-section-details">
                  {formData.decorationProcesses.length === 0 ? (
                    <div className="summary-detail-row">
                      <span className="summary-detail-label">No decoration processes</span>
                      <span></span>
                    </div>
                  ) : (
                    formData.decorationProcesses.map((dec, i) => (
                      <div key={i} className="summary-detail-row">
                        <span className="summary-detail-label">{formatSlug(dec.value)}</span>
                        <span className="summary-detail-value">
                          {dec.quantity ? `${Number(dec.quantity).toLocaleString()} stitches` : ""}
                        </span>
                      </div>
                    ))
                  )}
                  <div className="summary-detail-row" style={{ marginTop: 4 }}>
                    <span className="summary-detail-label">Decoration emissions</span>
                    <span className="summary-detail-value" style={{ color: "#2563eb", fontWeight: 600 }}>
                      {formatEmissionsDetail(formData.emissions.decoration)}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Packaging */}
            <div className="summary-section">
              <div className="summary-section-header" onClick={() => toggleSection("packaging")}>
                <div className="summary-section-left">
                  <div className="summary-section-icon packaging">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M3 5L9 2L15 5V13L9 16L3 13V5Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                      <path d="M3 5L9 8M9 8L15 5M9 8V16" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div className="summary-section-info">
                    <p className="summary-section-name">Packaging</p>
                    <p className="summary-section-preview">{getPackagingPreview()}</p>
                  </div>
                </div>
                <div className={`summary-section-chevron ${expandedSections.packaging ? "open" : ""}`}>
                  <ChevronDown />
                </div>
              </div>
              {expandedSections.packaging && (
                <div className="summary-section-details">
                  {formData.packaging.materials.length === 0 ? (
                    <div className="summary-detail-row">
                      <span className="summary-detail-label">No packaging materials</span>
                      <span></span>
                    </div>
                  ) : (
                    formData.packaging.materials.map((pkg, i) => (
                      <div key={i} className="summary-detail-row">
                        <span className="summary-detail-label">{pkg.type || `Package ${i + 1}`}</span>
                        <span className="summary-detail-value">{formatWeight(pkg.weight)}</span>
                      </div>
                    ))
                  )}
                  <div className="summary-detail-row" style={{ marginTop: 4 }}>
                    <span className="summary-detail-label">Packaging emissions</span>
                    <span className="summary-detail-value" style={{ color: "#3b82f6", fontWeight: 600 }}>
                      {formatEmissionsDetail(formData.emissions.packaging)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right — Product Social Impact */}
          <div className="summary-social-column">
            <div className="summary-card">
              <div className="summary-card-header">
                <h2 className="summary-card-title">Product &amp; Manufacturer Credentials <span className="text-muted font-1">(optional)</span></h2>
                <p className="summary-card-subtitle">highlight credentials for this product</p>
              </div>

              <div className="summary-social-section">
                <p className="summary-social-section-desc">Select any that apply to this product</p>
                <div className="summary-tags">
                  {PRODUCT_IMPACTS.map((item) => (
                    <button
                      key={item.id}
                      className={`summary-tag ${selectedProduct.includes(item.id) ? "selected" : ""}`}
                      onClick={() => toggleTag(selectedProduct, setSelectedProduct, item.id)}
                    >
                      <span className="summary-tag-check">
                        {selectedProduct.includes(item.id) && <CheckIcon />}
                      </span>
                      {item.label}
                    </button>
                  ))}
                  <button
                    className={`summary-tag ${selectedProduct.includes("other") ? "selected" : ""}`}
                    onClick={() => toggleTag(selectedProduct, setSelectedProduct, "other")}
                  >
                    <span className="summary-tag-check">
                      {selectedProduct.includes("other") && <CheckIcon />}
                    </span>
                    Other
                  </button>
                </div>
                {selectedProduct.includes("other") && (
                  <input
                    type="text"
                    className="summary-other-input"
                    placeholder="Describe other product credential..."
                    value={productOther}
                    onChange={(e) => setProductOther(e.target.value)}
                  />
                )}

                {/* Per-certification file uploads for selected product certs */}
                {selectedProduct.length > 0 && (
                  <div className="summary-cert-uploads">
                    {selectedProduct.map((certId) => {
                      const label = certId === "other"
                        ? (productOther || "Other")
                        : productCertLabel(certId);
                      const file = certFiles[certId];
                      const isUploading = uploadingCert === certId;

                      return (
                        <div key={certId} className="summary-cert-upload-row">
                          <div className="summary-cert-upload-label">{label}</div>
                          {file ? (
                            <div className="summary-file-item">
                              <div className="summary-file-icon">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M9 1H4C3.448 1 3 1.448 3 2V14C3 14.552 3.448 15 4 15H12C12.552 15 13 14.552 13 14V5L9 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                  <path d="M9 1V5H13" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                                </svg>
                              </div>
                              <div className="summary-file-info">
                                <div className="summary-file-name">{file.name}</div>
                                {file.size && <div className="summary-file-size">{formatFileSize(file.size)}</div>}
                              </div>
                              <button className="summary-file-remove" onClick={() => removeCertFile(certId)}>
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                  <path d="M3 3L11 11M11 3L3 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                              </button>
                            </div>
                          ) : isUploading ? (
                            <div className="summary-uploading">
                              <div className="summary-uploading-spinner"></div>
                              Uploading...
                            </div>
                          ) : (
                            <label className="summary-cert-upload-btn">
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M7 11V3M7 3L4 6M7 3L10 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                              Upload certificate
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                                style={{ display: "none" }}
                                onChange={(e) => handleCertFileUpload(certId, e)}
                              />
                            </label>
                          )}
                        </div>
                      );
                    })}
                    {selectedProduct.some((id) => !certFiles[id] && !NO_FILE_CERTS.includes(id)) && (
                      <p className="summary-cert-note">
                        Certifications without a supporting document still show on your results page, but uploading the certificate is recommended so customers can check it.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="summary-actions">
          <button className="summary-btn summary-btn-outline" onClick={handleDoAnother}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M6 12L2 8L6 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 8H10C12.2 8 14 9.8 14 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Do Another Assessment
          </button>
          <button className="summary-btn summary-btn-primary" onClick={handleViewResults}>
            See Public Results Page
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
